/**
 * Convert markdown source into clean, human-readable plain text for the clipboard.
 *
 * This is NOT a full markdown parser — it's a pragmatic stripper for chat content.
 * When a user copies an assistant reply they want the words, not the syntax: no
 * stray `**`, `##`, backticks, list markers, table pipes, or link/image URLs. We
 * remove that scaffolding while preserving the readable text and sensible line
 * breaks. Deliberately dependency-free (chat messages aren't pathological markdown).
 */
export function markdownToPlainText(md: string): string {
  if (!md) return "";
  let text = md.replace(/\r\n?/g, "\n");

  // Fenced code blocks: keep the inner code, drop the ``` / ~~~ fences + language tag.
  text = text.replace(/```[^\n]*\n([\s\S]*?)```/g, (_m, code: string) => code.replace(/\n$/, ""));
  text = text.replace(/~~~[^\n]*\n([\s\S]*?)~~~/g, (_m, code: string) => code.replace(/\n$/, ""));

  // Images: ![alt](url) -> alt text (or nothing). Links: [text](url) -> text.
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, (_m, alt: string) => alt || "");
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  text = text.replace(/\[([^\]]+)\]\[[^\]]*\]/g, "$1"); // reference-style links

  const lines = text.split("\n").map((line) => {
    let l = line;
    // Table separator rows (|---|:--:|) -> drop entirely.
    if (/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(l)) return "";
    // Table content rows: drop the outer pipes, turn inner pipes into spacing.
    if (/^\s*\|.*\|\s*$/.test(l)) {
      l = l
        .replace(/^\s*\|/, "")
        .replace(/\|\s*$/, "")
        .replace(/\s*\|\s*/g, "   ")
        .trim();
    }
    l = l.replace(/^\s*>\s?/, ""); // blockquote marker
    l = l.replace(/^\s{0,3}#{1,6}\s+/, ""); // ATX heading marker
    if (/^\s*([-*_])\1{2,}\s*$/.test(l)) return ""; // horizontal rule
    l = l.replace(/^(\s*)[-*+]\s+/, "$1• "); // unordered list marker -> bullet
    return l; // ordered "1. " markers read fine as plain text — leave them
  });
  text = lines.join("\n");

  // Inline emphasis / code (bold before italic so `**x**` isn't half-stripped).
  text = text.replace(/`([^`]+)`/g, "$1");
  text = text.replace(/(\*\*|__)(.*?)\1/g, "$2");
  text = text.replace(/(\*|_)(.*?)\1/g, "$2");
  text = text.replace(/~~(.*?)~~/g, "$1");

  // Tidy whitespace: collapse big gaps, drop trailing spaces, trim ends.
  return text
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]+$/gm, "")
    .trim();
}
